import React, { useState, useEffect } from 'react';
import { Mail, Send, X, Check, Loader2, AlertCircle } from 'lucide-react';
import './ContactWidget.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8005";

function ContactWidget({ currentUser }) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Sync initial user details if logged in
  useEffect(() => {
    if (currentUser && !currentUser.is_guest) {
      setName(prev => prev || currentUser.name || currentUser.username || '');
      setEmail(prev => prev || currentUser.email || '');
    }
  }, [currentUser, isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleOpen = () => {
    setError('');
    setSuccess(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setError('');
    if (success) {
      setMessage('');
      setSubject('');
      setSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Please provide your name.');
      return;
    }

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please provide a valid email address.');
      return;
    }

    if (!message.trim()) {
      setError('Please write a message.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim() || undefined,
          message: message.trim(),
          honeypot: honeypot || undefined
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send your message. Please try again later.');
      }

      setSuccess(true);
      setMessage('');
      setSubject('');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Trigger Button with little Mailbox item */}
      <button
        type="button"
        className="contact-floating-trigger"
        onClick={handleOpen}
        aria-label="Contact Support"
        title="Contact Us"
      >
        <Mail size={16} className="contact-trigger-icon" />
        <span className="contact-trigger-label">Contact</span>
      </button>

      {/* Contact Modal Dialog */}
      {isOpen && (
        <div
          className="contact-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <div className="contact-modal-card" role="dialog" aria-modal="true" aria-labelledby="contact-modal-title">
            <div className="contact-modal-header">
              <div className="contact-header-title-wrap">
                <div className="contact-header-icon-box">
                  <Mail size={18} color="#58a6ff" />
                </div>
                <div>
                  <h3 id="contact-modal-title" className="contact-title">Contact Us</h3>
                  <p className="contact-subtitle">Have a question or feedback? We’d love to hear from you.</p>
                </div>
              </div>
              <button
                type="button"
                className="contact-close-btn"
                onClick={handleClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="contact-modal-body">
              {success ? (
                <div className="contact-success-state">
                  <div className="contact-success-icon-box">
                    <Check size={32} color="#34d399" />
                  </div>
                  <h4 className="contact-success-title">Message Sent!</h4>
                  <p className="contact-success-desc">
                    Thank you for reaching out. We have received your note and will get back to you shortly.
                  </p>
                  <button
                    type="button"
                    className="contact-submit-btn"
                    onClick={handleClose}
                    style={{ marginTop: '16px' }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  {error && (
                    <div className="contact-error-banner">
                      <AlertCircle size={16} className="contact-error-icon" />
                      <span>{error}</span>
                    </div>
                  )}

                  {/* Honeypot field for bot detection (hidden from users) */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <label htmlFor="contact_hp">Leave this field blank</label>
                    <input
                      id="contact_hp"
                      type="text"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="contact-field-row">
                    <div className="contact-field-group">
                      <label className="contact-label" htmlFor="contact-name">Your Name</label>
                      <input
                        id="contact-name"
                        type="text"
                        className="contact-input"
                        placeholder="Alex Explorer"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>

                    <div className="contact-field-group">
                      <label className="contact-label" htmlFor="contact-email">Your Email</label>
                      <input
                        id="contact-email"
                        type="email"
                        className="contact-input"
                        placeholder="alex@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="contact-field-group">
                    <label className="contact-label" htmlFor="contact-subject">
                      Subject <span className="contact-optional">(optional)</span>
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      className="contact-input"
                      placeholder="e.g. Map idea, feedback, or suggestion"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="contact-field-group">
                    <label className="contact-label" htmlFor="contact-message">Message</label>
                    <textarea
                      id="contact-message"
                      className="contact-textarea"
                      placeholder="How can we help you?"
                      rows={5}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="contact-submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="contact-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={15} />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ContactWidget;
